import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LegalModal from '../ui/LegalModal';

const Footer = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');

  const openModal = (type) => {
    setModalType(type);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalType('');
  };
  return (
    <footer className="w-full bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 py-8 transition-colors duration-300">
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-16">
        <div className="max-w-7xl mx-auto">
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Copyright and Links */}
          <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-8 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium text-center md:text-left">©2025 DeviceLink Global Pvt. Ltd. — All rights reserved.</span>
            <div className="flex items-center space-x-4">
              <span className="text-gray-400 dark:text-gray-500">|</span>
              <motion.button 
                onClick={() => openModal('terms')}
                className="hover:text-sky-500 dark:hover:text-sky-400 transition-colors duration-200 font-medium"
                whileHover={{ y: -1 }}
              >
                Terms and Conditions
              </motion.button>
              <span className="text-gray-400 dark:text-gray-500">|</span>
              <motion.button 
                onClick={() => openModal('privacy')}
                className="hover:text-sky-500 dark:hover:text-sky-400 transition-colors duration-200 font-medium"
                whileHover={{ y: -1 }}
              >
                Privacy Policy
              </motion.button>
              <span className="text-gray-400 dark:text-gray-500">|</span>
              <motion.button 
                onClick={() => openModal('about')}
                className="hover:text-sky-500 dark:hover:text-sky-400 transition-colors duration-200 font-medium"
                whileHover={{ y: -1 }}
              >
                About Us
              </motion.button>
            </div>
          </div>

          {/* Social Media Icons */}
          <div className="flex items-center space-x-4">
            {/* Facebook */}
            <motion.a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white shadow-lg"
              whileHover={{ 
                scale: 1.1, 
                backgroundColor: "#1d4ed8",
                boxShadow: "0 10px 25px -5px rgba(59, 130, 246, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
            </motion.a>

            {/* Twitter */}
            <motion.a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-blue-400 rounded-full flex items-center justify-center text-white shadow-lg"
              whileHover={{ 
                scale: 1.1, 
                backgroundColor: "#0284c7",
                boxShadow: "0 10px 25px -5px rgba(56, 189, 248, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
              </svg>
            </motion.a>

            {/* YouTube */}
            <motion.a
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white shadow-lg"
              whileHover={{ 
                scale: 1.1, 
                backgroundColor: "#dc2626",
                boxShadow: "0 10px 25px -5px rgba(239, 68, 68, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </motion.a>

            {/* Instagram */}
            <motion.a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white shadow-lg"
              whileHover={{ 
                scale: 1.1,
                boxShadow: "0 10px 25px -5px rgba(168, 85, 247, 0.5)"
              }}
              whileTap={{ scale: 0.95 }}
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987c6.62 0 11.987-5.367 11.987-11.987C24.014 5.367 18.637.001 12.017.001zM15.156 7.987c.03.307.038.648.038.98v6.061c0 1.533-.123 2.304-.261 2.6a1.615 1.615 0 0 1-.923.923c-.297.139-1.068.262-2.6.262H9.351c-1.533 0-2.304-.123-2.6-.262a1.615 1.615 0 0 1-.923-.923c-.139-.296-.262-1.067-.262-2.6V8.967c0-1.533.123-2.304.262-2.6a1.615 1.615 0 0 1 .923-.923c.296-.139 1.067-.262 2.6-.262h2.059c1.533 0 2.304.123 2.6.262a1.615 1.615 0 0 1 .923.923c.139.296.262 1.067.262 2.6v.02zm-3.139 7.411c2.269 0 4.108-1.839 4.108-4.108S14.286 7.182 12.017 7.182s-4.108 1.839-4.108 4.108 1.839 4.108 4.108 4.108zm0-6.595c1.373 0 2.487 1.114 2.487 2.487S13.39 13.777 12.017 13.777s-2.487-1.114-2.487-2.487 1.114-2.487 2.487-2.487zm4.274-1.402a.96.96 0 1 1-1.92 0 .96.96 0 0 1 1.92 0z"/>
              </svg>
            </motion.a>
          </div>
        </motion.div>
        </div>
      </div>

      {/* Legal Modal */}
      <LegalModal 
        isOpen={modalOpen} 
        onClose={closeModal} 
        type={modalType} 
      />
    </footer>
  );
};

export default Footer;
